export interface TimeframeRenderedFrame<T> {
  readonly seriesKey: string;
  readonly data: T;
  readonly windowSecs: number;
  /** Anchored All viewport; carried with its frame while replacement history loads. */
  readonly historyStartTime?: number;
  /** Window covered by retained data while the viewport animates to a narrower target. */
  readonly dataWindowSecs?: number;
  readonly lineCurve: 'monotone' | 'linear' | 'rounded-linear';
}

export interface TimeframeRenderedFrameStore<
  T,
  TFrame extends TimeframeRenderedFrame<T> = TimeframeRenderedFrame<T>,
> {
  readonly get: () => TFrame | null;
  readonly remember: (frame: TFrame) => void;
}

/** Keep the last painted frame outside React state so Live samples do not cause extra renders. */
export function createTimeframeRenderedFrameStore<
  T,
  TFrame extends TimeframeRenderedFrame<T> = TimeframeRenderedFrame<T>,
>(): TimeframeRenderedFrameStore<T, TFrame> {
  let frame: TFrame | null = null;
  return {
    get: () => frame,
    remember: (nextFrame: TFrame) => {
      frame = nextFrame;
    },
  };
}

/** Transition-only geometry must never become the source of a later transition. */
export function rememberCommittedTimeframeRenderedFrame<
  T,
  TFrame extends TimeframeRenderedFrame<T>,
>(store: TimeframeRenderedFrameStore<T, TFrame>, renderedFrame: TFrame, targetFrame: TFrame): void {
  if (renderedFrame !== targetFrame) return;
  store.remember(targetFrame);
}

export function isTimeframeViewportTransitionComplete(args: {
  readonly retainOutgoingData: boolean;
  readonly transitionGeneration: number;
  readonly completedTransitionGeneration: number | null;
}): boolean {
  return (
    !args.retainOutgoingData || args.completedTransitionGeneration === args.transitionGeneration
  );
}

/**
 * Retain a coherent frame while another timeframe of the same series loads.
 * When the viewport shrinks, keep the wider source data under the target
 * viewport until Liveline finishes its window animation; otherwise the narrow
 * target dataset is clipped against the still-wide animated viewport.
 */
export function resolveTimeframeRenderedFrame<T>(args: {
  readonly target: TimeframeRenderedFrame<T>;
  readonly targetReady: boolean;
  readonly capturedFrame: TimeframeRenderedFrame<T> | null;
  readonly viewportTransitionComplete: boolean;
}): TimeframeRenderedFrame<T> | null {
  const { target, targetReady, capturedFrame, viewportTransitionComplete } = args;
  if (capturedFrame?.seriesKey !== target.seriesKey) return targetReady ? target : null;
  if (!targetReady) return capturedFrame;
  if (!viewportTransitionComplete && capturedFrame.windowSecs > target.windowSecs) {
    return {
      seriesKey: target.seriesKey,
      data: capturedFrame.data,
      windowSecs: target.windowSecs,
      ...(target.historyStartTime === undefined
        ? {}
        : { historyStartTime: target.historyStartTime }),
      dataWindowSecs: capturedFrame.dataWindowSecs ?? capturedFrame.windowSecs,
      lineCurve: capturedFrame.lineCurve,
    };
  }
  return target;
}
